import React from "react";
import {
  Box,
  IconButton,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
} from "@mui/material";
import {
  DropDownArrowIcon,
  RequireIcon as CustomRequireIcon,
} from "@ippon/shared-assets";
import CloseIcon from "@mui/icons-material/Close";
import { AiBadge, CustomButton, colors } from "@ippon/shared-components";
import { Can } from "@/components/auth/Can";
import type {
  ExpenseBreakdownItem,
  ExpenseBreakdownTaxCategory,
} from "@/store/expenseSlice";
import {
  isBillingAmountTooLong,
  BILLING_AMOUNT_MAX_DIGITS,
} from "@/components/PropertyDetail/shared/BillingItemsTable";
import {
  parseExpenseBreakdownAmount,
  updateExpenseBreakdownAmount,
} from "./expenseBreakdownInput";

// 収入 (BillingItemsTable) と同じ税区分。非課税 / 課税10%。
const TAX_OPTIONS: { value: ExpenseBreakdownTaxCategory; label: string }[] = [
  { value: "non_taxable", label: "非課税" },
  { value: "taxable", label: "課税10%" },
];

interface Props {
  items: ExpenseBreakdownItem[];
  onChange: (next: ExpenseBreakdownItem[]) => void;
  /** AI が自動入力した item_id の Set。バッジ表示用 */
  aiFilledItemIds?: Set<string>;
  /** 送信時など、金額必須エラーを表示する対象 item_id。 */
  amountRequiredErrorItemIds?: Set<string>;
  /** 表示位置調整用の上余白 */
  sx?: { mt?: number };
}

/**
 * 支出登録モーダルの「内訳」テーブル。
 *
 * UI 設計:
 *  - 親側で `CustomSectionTitle title="内訳"` を出すため、ここでは外枠 / 自前タイトル無し
 *  - ヘッダー行は CustomTable と同じ薄グレー背景 (#ebebeb)
 *  - 列: 項目 / 金額 / [削除]
 *  - 行追加はカスタムフォーム設定の「新しい項目を追加」と同じ
 *    dashed clearPrimary ボタンをテーブル末尾に表示
 */
const ExpenseBreakdownTable: React.FC<Props> = ({
  items,
  onChange,
  aiFilledItemIds,
  amountRequiredErrorItemIds,
  sx,
}) => {
  const isAi = (id: string) => aiFilledItemIds?.has(id) ?? false;

  // フォーカス中の編集ドラフト (CustomTextInput showYen と同じ挙動を再現)。
  // フォーカス時に String(amount) を入れ、入力中はそのまま、blur で NFKC 正規化 → 数値化。
  const [amountDrafts, setAmountDrafts] = React.useState<
    Record<string, string>
  >({});
  const [touchedAmountItemIds, setTouchedAmountItemIds] = React.useState<
    Set<string>
  >(() => new Set());

  const updateRow = (id: string, field: "item", rawValue: string) => {
    const next = items.map((row) =>
      row.item_id === id ? { ...row, [field]: rawValue } : row,
    );
    onChange(next);
  };

  const updateTaxCategory = (
    id: string,
    value: ExpenseBreakdownTaxCategory,
  ) => {
    onChange(
      items.map((row) =>
        row.item_id === id ? { ...row, tax_category: value } : row,
      ),
    );
  };

  const handleAmountFocus = (id: string, current: number) => {
    setAmountDrafts((prev) => ({
      ...prev,
      [id]: current === 0 ? "" : String(current),
    }));
  };

  const handleAmountChange = (id: string, value: string) => {
    // 桁数上限を超える入力は受け付けない (13桁目以降を打てない)。
    if (value.replace(/[^0-9]/g, "").length > BILLING_AMOUNT_MAX_DIGITS) return;
    setAmountDrafts((prev) => ({ ...prev, [id]: value }));

    // 表示用draftを維持したまま、解釈可能な金額は入力時点で親へ反映する。
    // 親のbreakdownDirtyと登録ボタンをblur待ちにしない。
    const next = updateExpenseBreakdownAmount(items, id, value);
    if (next) onChange(next);
  };

  const handleAmountBlur = (id: string) => {
    setTouchedAmountItemIds((prev) => new Set(prev).add(id));
    const raw = amountDrafts[id];
    if (raw === undefined) return;
    const parsed = parseExpenseBreakdownAmount(raw);
    setAmountDrafts((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
    if (parsed === null) return; // 解釈不能 → 値を保持
    const next = items.map((row) =>
      row.item_id === id ? { ...row, amount: parsed } : row,
    );
    onChange(next);
  };

  const formatAmount = (n: number) =>
    n === 0 ? "" : Number(n).toLocaleString();

  const addRow = () => {
    const newItem: ExpenseBreakdownItem = {
      item_id: `bd_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      item: "",
      amount: 0,
      tax_category: "taxable",
    };
    onChange([...items, newItem]);
  };

  const removeRow = (id: string) => {
    onChange(items.filter((row) => row.item_id !== id));
  };

  const headerCellSx = {
    fontSize: 13,
    fontWeight: 600,
    color: colors.text.primary,
    backgroundColor: "#F5F5F5",
    borderBottom: `1px solid ${colors.border.tab}`,
    py: 1,
    position: "relative" as const,
  } as const;

  const headerCellWithSeparatorSx = {
    ...headerCellSx,
    "&::after": {
      content: '""',
      position: "absolute",
      right: 0,
      top: "6px",
      bottom: "6px",
      width: "1px",
      backgroundColor: "rgba(224, 224, 224, 1)",
    },
  } as const;

  const bodyCellSx = {
    p: 1,
    borderBottom: `1px solid ${colors.border.light}`,
    // 金額セルに helperText(必須エラー)が出ると行の高さが増える。既定の
    // verticalAlign:middle だと項目名・税区分が中央維持で下にズレるため、
    // 上揃えにして「伸びた分だけ下に広がる」挙動にする。
    verticalAlign: "top" as const,
  } as const;

  // 税区分セレクト。予算 (Budget) のインラインセレクトと同じ見た目:
  // DropDownArrowIcon / 高さ 34px(border-box) / borderRadius 8 / border #D9D9D9 相当。
  // border-box 同士なので項目名・金額の TextField (height 34) と実寸がピッタリ揃う。
  const selectSx = {
    height: 34,
    fontSize: 13,
    borderRadius: "8px",
    backgroundColor: "white",
    "& .MuiSelect-select": { padding: "8px 32px 8px 14px" },
    "& .MuiOutlinedInput-notchedOutline": { borderColor: colors.border.light },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: colors.border.light,
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: colors.border.light,
    },
    // 矢印は非正方形svg(14x9)。MUI 既定の em ベース縦センタリングだとズレるため、
    // 予算 (Budget) のインラインセレクトと同じく top:50% + translateY で中央寄せ。
    "& .MuiSelect-icon": {
      fontSize: 14,
      top: "50%",
      transform: "translateY(-50%)",
      right: "12px !important",
    },
    "& .MuiSelect-iconOpen": {
      transform: "translateY(-50%) rotate(180deg) !important",
    },
  } as const;

  const cellInputSx = {
    "& .MuiOutlinedInput-root": {
      // 税区分セレクトと同じ高さ (border-box 34px) に揃える。
      height: 34,
      fontSize: 13,
      borderRadius: "8px",
      backgroundColor: "white",
      "& fieldset": { borderColor: colors.border.light },
      "&:hover fieldset": { borderColor: colors.border.light },
      "&.Mui-focused fieldset": { borderColor: colors.border.light },
    },
    "& input": { fontVariantNumeric: "tabular-nums" as const },
  } as const;

  return (
    <Box sx={{ mt: sx?.mt ?? 2 }}>
      <Box
        sx={{
          border: `1px solid ${colors.border.tab}`,
          borderRadius: "8px",
          // 角丸クリップは維持しつつ、明細項目の minWidth で合計が容器を超える
          // 狭い画面では横スクロールさせる (切れ防止)。
          overflowX: "auto",
          overflowY: "hidden",
        }}
      >
        <Box>
          <Table size="small" sx={{ width: "100%", tableLayout: "fixed" }}>
            <TableHead>
              <TableRow>
                {/* 内訳の「項目」列。予実の明細項目(科目)とは別レイヤなので「項目」と呼ぶ。
                      幅指定なし=残り幅を全部取り一番広い列にしつつ、minWidth で下限を確保。 */}
                <TableCell sx={{ ...headerCellWithSeparatorSx, width: "42%" }}>
                  項目
                </TableCell>
                <TableCell sx={{ ...headerCellWithSeparatorSx, width: "20%" }}>
                  税区分
                </TableCell>
                {/* 金額は固定幅 (12桁 + 円 が収まる程度)。 */}
                <TableCell sx={{ ...headerCellSx, width: "30%" }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                    }}
                  >
                    金額
                    <CustomRequireIcon />
                  </Box>
                </TableCell>
                <TableCell sx={{ ...headerCellSx, width: "8%", p: 0 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((row) => {
                const ai = isAi(row.item_id);
                const amountDraft = amountDrafts[row.item_id];
                const currentAmount =
                  amountDraft !== undefined
                    ? parseExpenseBreakdownAmount(amountDraft)
                    : row.amount;
                const amountRequiredError =
                  ((amountRequiredErrorItemIds?.has(row.item_id) ?? false) ||
                    touchedAmountItemIds.has(row.item_id)) &&
                  Number(currentAmount || 0) <= 0;
                // 桁数オーバー (12桁超) も金額欄直下に表示する。編集中の draft を優先。
                const amountTooLong = isBillingAmountTooLong(
                  amountDraft ?? row.amount,
                );
                const amountErrorText = amountRequiredError
                  ? "金額は必須です"
                  : amountTooLong
                    ? `${BILLING_AMOUNT_MAX_DIGITS}桁以内で入力してください`
                    : "";
                return (
                  <TableRow key={row.item_id}>
                    <TableCell sx={bodyCellSx}>
                      <Stack direction="row" alignItems="center" spacing={0.75}>
                        <TextField
                          size="small"
                          fullWidth
                          value={row.item}
                          onChange={(e) =>
                            updateRow(row.item_id, "item", e.target.value)
                          }
                          placeholder="項目名"
                          sx={cellInputSx}
                        />
                        {ai && <AiBadge />}
                      </Stack>
                    </TableCell>
                    <TableCell sx={bodyCellSx}>
                      <Select
                        size="small"
                        fullWidth
                        value={row.tax_category || "taxable"}
                        onChange={(e) =>
                          updateTaxCategory(
                            row.item_id,
                            e.target.value as ExpenseBreakdownTaxCategory,
                          )
                        }
                        IconComponent={DropDownArrowIcon}
                        sx={selectSx}
                      >
                        {TAX_OPTIONS.map((o) => (
                          <MenuItem
                            key={o.value}
                            value={o.value}
                            sx={{ fontSize: 13 }}
                          >
                            {o.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </TableCell>
                    <TableCell sx={{ ...bodyCellSx, pr: 0 }}>
                      <Stack
                        direction="row"
                        alignItems="flex-start"
                        spacing={0.75}
                      >
                        <Box sx={{ position: "relative", flex: 1 }}>
                          <TextField
                            size="small"
                            fullWidth
                            value={
                              amountDrafts[row.item_id] !== undefined
                                ? amountDrafts[row.item_id]
                                : formatAmount(row.amount)
                            }
                            onFocus={() =>
                              handleAmountFocus(row.item_id, row.amount)
                            }
                            onChange={(e) =>
                              handleAmountChange(row.item_id, e.target.value)
                            }
                            onBlur={() => handleAmountBlur(row.item_id)}
                            inputMode="numeric"
                            error={amountRequiredError || amountTooLong}
                            helperText={amountErrorText}
                            InputProps={{
                              endAdornment: (
                                <Box
                                  component="span"
                                  sx={{
                                    fontSize: 13,
                                    color: colors.text.primary,
                                    ml: 0.5,
                                  }}
                                >
                                  円
                                </Box>
                              ),
                            }}
                            sx={{
                              ...cellInputSx,
                              "& input": {
                                textAlign: "left",
                                fontVariantNumeric: "tabular-nums",
                              },
                            }}
                          />
                        </Box>
                        {ai && <AiBadge />}
                      </Stack>
                    </TableCell>
                    <TableCell
                      sx={{
                        ...bodyCellSx,
                        px: 0,
                        textAlign: "center",
                      }}
                    >
                      {/* セルは上揃え (verticalAlign:top)。入力欄(高さ34px)の
                            中心にアイコンを合わせるため、同じ高さの箱で中央寄せ。 */}
                      <Box
                        sx={{
                          height: 34,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <Can action="config:edit">
                          <IconButton
                            size="small"
                            aria-label="行を削除"
                            onClick={() => removeRow(row.item_id)}
                            sx={{ color: colors.icon.primary, p: 0.25 }}
                          >
                            <CloseIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Can>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
              <TableRow>
                <TableCell
                  colSpan={4}
                  sx={{
                    p: 1,
                    borderBottom: "none",
                  }}
                >
                  <Can action="config:edit">
                    <CustomButton
                      label="新しい項目を追加"
                      buttonCategory="clearPrimary"
                      onClick={addRow}
                      sx={{ width: "100%", borderStyle: "dashed" }}
                    />
                  </Can>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Box>
      </Box>
    </Box>
  );
};

export default ExpenseBreakdownTable;
